import axios from 'axios'

const state = {
    domain: ab.infinityCustomDomain,
    status: null,
    message: null,
    wizardOpen: false,
    verification: {
        lastVerified: null,
        verifying: false,
        verified: false,
        success: false,
        error: null,
    },
    save: {
        saving: false,
        saved: false,
        error: null,
    },
    remove: {
        removing: false,
        removed: false,
        error: null,
    },
    messages: {
        invalidDomainName: 'Please enter a valid domain name, do not include "http://" and do not use a subdomain. Enter the root domain only. E.g. "domain.com".',
        domainAlreadyLinked: 'This domain is already linked to another store.',
        hostNotUpdated: 'Looks like your DNS records still haven\'t updated. Please wait a few hours and try again. If you are still seeing this message after 48 hours please contact our support.',
        domainSaved: {
            title: 'Domain Linked',
            message: 'Your domain name has been successfully linked, great work! Your domain should be active within a few hours.',
        },
        confirmUnlink: {
            title: 'Confirm Unlink',
            message: 'Are you sure you want to unlink your custom domain name?'
        },
        unknownError: (customMessage = '', withOups= true) => `${withOups ? 'Oops! ' : ''}Something went wrong, ${customMessage}please try again in a few moments.`
    }
};

const getters = {
    domain: (state) => state.domain,
    wizardOpen: (state) => state.wizardOpen,
    verification: (state) => state.verification,
    verifying: (state) => state.verification.verifying,
    verified: (state) => state.verification.verified,
    verificationErrorType: (state) => state.verification.errorType,
    verificationError: (state) => state.verification.error,
    lastVerified: (state) => state.verification.lastVerified,
    save: (state) => state.save,
    saving: (state) => state.save.saving,
    saved: (state) => state.save.saved,
    saveError: (state) => state.save.error,
    remove: (state) => state.remove,
    removing: (state) => state.remove.removing,
    removed: (state) => state.remove.removed,
    removeError: (state) => state.remove.error,
    messages: (state) => state.messages,

    apiUrl: () => ab.apiUrl,
    apiToken: () => ab.token,
    currentDomain: () => ab.baseUrl && ab.baseUrl.replace('https://', '').toLowerCase(),
    customDomainConfig: () => ab.infinityCustomDomainConfig // Must follow this format: {records: [{type, name, value, ttl}]}
};

const mutations = {
    domain(state, payload) {
        state.domain = payload;
    },
    wizardStep(state, payload) {
        state.wizardOpen = payload;
    },
    wizardOpen(state, payload) {
        state.wizardOpen = payload;
    },
    verification(state, payload) {
        state.verification = payload;
    },
    save(state, payload) {
        state.save = payload;
    },
    remove(state, payload) {
        state.remove = payload;
    }
};

const actions = {
    async verifyDomain({getters, commit, rootState}, domain) {
        let verification = {
            lastVerified: domain,
            verifying: true,
            verified: false,
            errorType: null, // 'validation' | 'host not updated' | 'unknown'
            error: null,
        };
        commit('verification', verification);
        try {
            await axios.post(`${getters.apiUrl}/infinity/domain/validate`, {
                api_token: getters.apiToken,
                domain_name: domain
            });
            verification.verified = true;
        } catch (error) {
            const status = error && error.response && error.response.status;
            const message = error && error.response && error.response.data.error.message;

            verification.verified = false;

            if (status === 422) {
                verification.error = getters.messages.invalidDomainName;
            } else if (status === 404) {
                verification.error = getters.messages.hostNotUpdated;
                //todo: [Enhancement] - Should check with a constant error code not a message that could be changed
            } else if (status === 403 && message === 'This domain is already linked to another store.') {
                verification.error = getters.messages.domainAlreadyLinked;
            } else {
                verification.error = getters.messages.unknownError();
            }
        } finally {
            verification.verifying = false;
            commit('verification', verification);
        }
    },

    async saveDomain({commit, getters}) {
        let save = {
            saving: true,
            saved: false,
            error: null,
        };
        commit('save', save);
        try {
            await axios.post(`${getters.apiUrl}/infinity/domain/save`, {
                api_token: getters.apiToken,
                domain_name: getters.lastVerified
            });
            save.saved = true;
            commit('domain', getters.lastVerified);
        } catch (error) {
            save.error = getters.messages.unknownError('your custom domain name has not been saved, ', false);
        } finally {
            save.saving = false;
            commit('save', save);
        }
    },

    async removeDomain({commit, getters}) {
        let remove = {
            removing: true,
            removed: false,
            error: null,
        };
        commit('remove', remove);
        try {
            await axios.delete(`${getters.apiUrl}/infinity/domain/delete`, {
                api_token: getters.apiToken,
            });
            remove.removed = true;
            commit('domain', null);
        } catch (error) {
            remove.error = getters.messages.unknownError('your custom domain name has not been removed, ', false);
        } finally {
            remove.removing = false;
            commit('remove', remove);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}